import uniq from 'lodash/uniq';

// Edges eligible for the upsertMemberByLabel save payload: excludes read-only org roles,
// the org-level OWNER edge (owner's org role is not editable from this roster), and
// inactive edges that were never persisted (edgeId === null && !isActive).
export const getSaveableEdges = (personEdges, { orgId, orgReadOnlyRoles = [], ownerLabel }) =>
  personEdges.filter(
    ({ edge }) =>
      !(edge.to === orgId && orgReadOnlyRoles.includes(edge.label)) &&
      !(edge.to === orgId && edge.label === ownerLabel) &&
      (edge.edgeId !== null || (edge.edgeId === null && edge.isActive)),
  );

/**
 * SLACKERS-623: pull the showable explanation out of a rejected roster mutation, or null if
 * there isn't one.
 *
 * The server sends a sentence written for the person who hit the refusal, but it arrives
 * wrapped twice: the shared axios interceptor prefixes "Request failed with status code NNN : "
 * and the server keeps an "Access Denied: " prefix for API consumers that match on it.
 * Stripping the status code is not just cosmetic — ErrorMessage swaps any message containing
 * "403" for a generic Access Denied page, discarding the text.
 *
 * Only a refusal carries something a user can act on. A network drop or a 500 gets null so the
 * caller falls back to its own wording rather than showing transport noise.
 */
export const refusalMessage = (error) => {
  const raw = error?.graphQLErrors?.[0]?.message || error?.message || '';
  const withoutTransport = raw.replace(/^Request failed with status code \d+\s*:\s*/, '');
  const explanation = withoutTransport.match(/^Access Denied:\s*([\s\S]+)$/);

  return explanation ? explanation[1].trim() || null : null;
};

export const formatPeople = (people = [], usages = [], orgNode = {}) => {
  let users = [];

  users = people?.length
    ? Object.values(
        people.reduce((acc, curr) => {
          const { node, edge, usageRoles } = curr;
          acc[node.id] = !acc[node.id]
            ? { ...node, orgRoles: [{ node: orgNode, edge }], usageRoles }
            : {
                ...acc[node.id],
                orgRoles: [...acc[node.id].orgRoles, { node: orgNode, edge }],
              };
          return acc;
        }, []),
      )
    : [];

  return users.map((person) => {
    const { id, email, firstName, lastName, orgRoles, usageRoles } = person;
    return {
      id,
      name: `${lastName}, ${firstName}`,
      email,
      firstName,
      lastName,
      orgRoles: uniq(orgRoles),
      ...(usages || []).reduce((acc, curr) => {
        acc[curr.node.id] = (usageRoles || [])
          .filter(({ node }) => node.id === curr.node.id)
          .map(({ edge }) => edge.label);
        return acc;
      }, {}),
      person,
    };
  });
};
